<script>
import TagChips from '@modules/crm/components/client-tags/client-tags.vue'
import AlertUpdateAsset from '@modules/consolidator/components/alert-update-asset/alert-update-asset'

export default {
  name: 'AssetTags',
  components: { TagChips, AlertUpdateAsset },
  props: {
    asset: { type: Object, default: () => ({}) },
    assetView: { type: Object, default: () => ({}) },
    composicaoFundos: { type: Object, default: () => ({}) },
    readonly: { type: Boolean, default: false },
  },
  computed: {
    isConsultantView() {
      return !this.$currentUser().IS_CLIENTE
    },
    tagsTextList() {
      return this.normalizeTags({ tags: this.asset.tags || [] })
    },
    hasFeeExempt() {
      return !!this.asset.isento_fee
    },
    hasDesconsiderarRentabilidade() {
      return !!this.asset.desconsiderar_rentabilidade
    },
    hasTaxExempt() {
      return !!this.asset.isento
    },
    hasIlliquid() {
      return !!this.asset.ativo_iliquido
    },
    hasMarketMarking() {
      return !!this.assetView.showMM
    },
    hasAlertUpdate() {
      return !!this.$currentUser().IS_INTERNAL
    },
    hasComposicao() {
      const items = this.composicaoFundos[this.asset.id] || []
      return items.length > 0
    },
    hasWarnings() {
      return !!this.asset.warnings
    },
    hasTags() {
      return this.isConsultantView && this.tagsTextList.length > 0
    },
    warningText() {
      if (!this.asset.warnings) return ''
      return Object.values(this.asset.warnings).join('\n')
    },
  },
  methods: {
    handleOpenDetalhes({ action } = {}) {
      this.$emit('open-detalhes', { asset: this.asset, action })
    },
    handleOpenNewAsset() {
      this.$emit('open-new-asset', { asset: this.asset })
    },
    normalizeTags({ tags } = {}) {
      const list = Array.isArray(tags) ? tags : []
      const normalized = []
      for (let i = 0; i < list.length; i++) {
        const text = this.tagToText({ tag: list[i] })
        if (text) normalized.push(text)
      }
      return normalized
    },
    tagToText({ tag } = {}) {
      if (!tag) return ''
      if (typeof tag === 'string') return tag
      if (typeof tag === 'number') return String(tag)
      if (typeof tag === 'object') {
        return String(tag.name || tag.label || tag.text || tag.value || '')
      }
      return String(tag)
    },
  },
}
</script>

<template>
  <div class="asset-tags">
    <div v-if="hasFeeExempt" class="asset-tags__item asset-tags__item--fee">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <div v-on="on">
            <v-chip
              class="asset-tags__chip asset-tags__chip--line-through"
              color="success"
              x-small
            >
              fee
            </v-chip>
          </div>
        </template>
        <span> Isento no cálculo de Fee </span>
      </v-tooltip>
    </div>

    <div v-if="hasTaxExempt" class="asset-tags__item asset-tags__item--tax">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <div v-on="on">
            <v-chip class="asset-tags__chip" color="primary" x-small>
              <v-icon small> mdi-currency-usd-off </v-icon>
            </v-chip>
          </div>
        </template>
        <span> Ativo isento de IR </span>
      </v-tooltip>
    </div>

    <div v-if="hasIlliquid" class="asset-tags__item asset-tags__item--illiquid">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <div v-on="on">
            <v-chip class="asset-tags__chip" x-small color="white">
              <v-icon small color="info">mdi-domain</v-icon>
            </v-chip>
          </div>
        </template>
        <span> Ativo ilíquido </span>
      </v-tooltip>
    </div>

    <div
      v-if="hasDesconsiderarRentabilidade"
      class="asset-tags__item asset-tags__item--desconsiderar-rentabilidade"
    >
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <div v-on="on">
            <v-chip
              class="asset-tags__chip asset-tags__chip--line-through"
              color="warning"
              x-small
            >
              %
            </v-chip>
          </div>
        </template>
        <span> Desconsiderado na rentabilidade da carteira </span>
      </v-tooltip>
    </div>

    <div v-if="hasMarketMarking" class="asset-tags__item asset-tags__item--mm">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <div v-on="on">
            <v-chip class="asset-tags__chip" color="info" x-small>MM</v-chip>
          </div>
        </template>
        <span> Marcação a mercado </span>
      </v-tooltip>
    </div>

    <div
      v-if="hasAlertUpdate"
      class="asset-tags__item asset-tags__item--update"
    >
      <AlertUpdateAsset :date="asset._d_referencia_pl" noSpacing />
    </div>

    <div
      v-if="hasComposicao"
      class="asset-tags__item asset-tags__item--composition"
    >
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <div v-on="on">
            <v-btn
              small
              icon
              class="asset-tags__icon-button"
              @click="handleOpenDetalhes({ action: 'composicao' })"
            >
              <v-icon small color="primary"> mdi-chart-pie </v-icon>
            </v-btn>
          </div>
        </template>
        <span>
          Este ativo possui uma composição.
          <br />
          Clique ou abra o menu <b>Ver detalhes</b> para visualizar.
        </span>
      </v-tooltip>
    </div>

    <div v-if="hasWarnings" class="asset-tags__item asset-tags__item--warning">
      <v-tooltip right>
        <template v-slot:activator="{ on }">
          <div v-on="on">
            <v-btn
              small
              icon
              class="asset-tags__icon-button"
              :disabled="readonly"
              @click="handleOpenNewAsset"
            >
              <v-icon small color="warning"> mdi-alert-circle-outline </v-icon>
            </v-btn>
          </div>
        </template>
        <span class="asset-tags__warning-text">
          {{ warningText || 'Com alertas' }}
        </span>
      </v-tooltip>
    </div>

    <div
      v-if="hasTags"
      class="asset-tags__item asset-tags__item--tags asset-tags__list"
    >
      <TagChips
        :tags="tagsTextList"
        :can-edit="false"
        colorMode="fixed"
        use-small-chips
      />
    </div>
  </div>
</template>

<style lang="sass">
.asset-tags
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 4px

  &__chip
    &--line-through
      text-decoration: line-through

  &__warning-text
    white-space: pre-line

  &__list
    max-width: 250px
</style>
